import React, { FC, PropsWithChildren, useCallback } from "react";

import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";

import { ScrollToTop } from "@/components/atoms";
import { UrlManager } from "@/shared/apis/UrlManager";
import { ViewMode } from "@/shared/contexts";
import { useRouterHook, useViewMode } from "@/shared/hooks";

import AuthenticatedHeader from "../Authenticated/components/AuthenticatedHeader/AuthenticatedHeader";
import Breadcrumbs from "../Authenticated/components/Breadcrumbs/Breadcrumbs";
import { StyledAuthPageBody, StyledAuthPageContainer, StyledMainHeader, StyledPageContainer } from "../styled";

type PublicLayoutProps = {
    breadcrumbs?: boolean;
    title?: string;
    onRedirectTo?: (url: string) => void;
};

const PublicLayout: FC<PropsWithChildren<PublicLayoutProps>> = ({
    children,
    breadcrumbs = false,
    title,
    onRedirectTo,
}) => {
    const { viewMode } = useViewMode();
    const { i18n } = useTranslation();
    const {
        globalState: { officeId },
    } = useRouterHook();

    const CustomHelmet = useCallback(() => {
        return (
            <Helmet>
                <html lang={i18n.language} />
                <title>{title}</title>
                <body data-custom-theme={officeId} />
                {officeId && <link rel="stylesheet" href={UrlManager.brokerSchema(officeId)} />}
            </Helmet>
        );
    }, [i18n.language]);

    return (
        <StyledAuthPageContainer>
            <CustomHelmet />
            {viewMode === ViewMode.FULL && (
                <StyledMainHeader>
                    <AuthenticatedHeader
                        showBrokerSelector={false}
                        onRedirectTo={onRedirectTo}
                        officeId={officeId}
                        disableSearch
                    />
                </StyledMainHeader>
            )}
            <StyledAuthPageBody className="authBody">
                <ScrollToTop />
                <StyledPageContainer>
                    {viewMode === ViewMode.FULL && breadcrumbs && <Breadcrumbs />}
                    {children}
                </StyledPageContainer>
            </StyledAuthPageBody>
        </StyledAuthPageContainer>
    );
};

export default PublicLayout;
